import * as Sentry from '@sentry/angular';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    __ZHMYAK_DASHBOARD_CONFIG__?: {
      sentryDsn?: string;
      sentryRelease?: string;
      sentryEnvironment?: string;
    };
  }
}

export function initSentry(): void {
  const runtimeConfig = window.__ZHMYAK_DASHBOARD_CONFIG__;
  const dsn = runtimeConfig?.sentryDsn || environment.sentry.dsn;

  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: runtimeConfig?.sentryEnvironment || environment.sentry.environment,
    release: runtimeConfig?.sentryRelease || environment.sentry.release || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracePropagationTargets: [
      window.location.origin,
      environment.apiBaseUrl,
    ],
    tracesSampleRate: 0.1,
    beforeSend(event) {
      event.request = event.request ? {
        ...event.request,
        url: scrubUrl(event.request.url),
      } : event.request;
      event.message = scrubSensitiveValue(event.message);
      event.exception = event.exception ? scrubSensitiveValue(event.exception) : event.exception;
      event.breadcrumbs = event.breadcrumbs ? scrubSensitiveValue(event.breadcrumbs) : event.breadcrumbs;
      event.contexts = event.contexts ? scrubSensitiveValue(event.contexts) : event.contexts;
      return event;
    },
  });

  Sentry.setTags({
    app: 'dashboard',
    apiBaseUrl: environment.apiBaseUrl,
  });
}

const SENSITIVE_KEYS = ['access_token', 'refresh_token', 'token', 'register_token', 'reset_token', 'password', 'email'];

export function scrubUrl(value: string | undefined): string | undefined {
  if (!value) {
    return value;
  }

  let url: URL | null = null;
  try {
    url = new URL(value);
  } catch {
    url = null;
  }

  if (url && (url.protocol === 'http:' || url.protocol === 'https:')) {
    // Сбрасываем фрагмент целиком: OAuth-стиль #access_token=… и любой иной
    // hash-пейлоад не должны попадать в Sentry (в дашборде нет hash-роутинга).
    url.hash = '';
    for (const key of [...url.searchParams.keys()]) {
      if (SENSITIVE_KEYS.includes(key.toLowerCase())) {
        url.searchParams.set(key, '[Filtered]');
      }
    }
    return url.toString();
  }

  // Не абсолютный http(s)-URL: свободный текст / относительная ссылка — не
  // переписываем в URL, лишь вырезаем встроенные секреты key=value после ?, & или #.
  return value
    .replace(/([?&#](?:access_token|refresh_token|token|register_token|reset_token|password|email)=)[^&#]+/gi, '$1[Filtered]');
}

function scrubSensitiveValue<T>(value: T): T {
  if (typeof value === 'string') {
    return scrubUrl(value) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubSensitiveValue(item)) as T;
  }
  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
      key,
      SENSITIVE_KEYS.includes(key.toLowerCase()) ? '[Filtered]' : scrubSensitiveValue(entry),
    ])
  ) as T;
}
